import { cycleCountIdFormat } from "@redotech/redo-inventory-id";
import { productIdFormat } from "@redotech/redo-product-id";
import { storageLocationIdFormat } from "@redotech/redo-warehouse-id";
import { unaryRpc } from "@redotech/rpc/definition";
import * as z from "zod";

const dateRangeSchema = z.object({ start: z.date(), end: z.date() });
export type CycleCountAnalyticsDateRange = z.infer<typeof dateRangeSchema>;

const weeklyAccuracyBucketSchema = z.object({
  weekStart: z.date(),
  countsRun: z.int(),
  itemsCounted: z.int(),
  varianceItems: z.int(),
});
export type CycleCountWeeklyAccuracyBucket = z.infer<
  typeof weeklyAccuracyBucketSchema
>;

const accuracyWeeksSchema = z.array(weeklyAccuracyBucketSchema);

const overviewOutputSchema = z.object({
  itemsCounted: z.int(),
  varianceItems: z.int(),
  uncostedVarianceItems: z.int(),
  skusCounted: z.int(),
  countsCompleted: z.int(),
  countsNeedsReview: z.int(),
  varianceShortage: z.number(),
  varianceOverage: z.number(),
  currency: z.string(),
  prior: z
    .object({
      itemsCounted: z.int(),
      varianceItems: z.int(),
      varianceShortage: z.number(),
      varianceOverage: z.number(),
    })
    .nullable(),
});
export type CycleCountAnalyticsOverview = z.infer<typeof overviewOutputSchema>;

const weekAxisSchema = z.array(z.date());

const accuracyOutputSchema = z.object({
  weekAxis: weekAxisSchema,
  weeks: accuracyWeeksSchema,
  byLocation: z.array(
    z.object({ locationName: z.string(), weeks: accuracyWeeksSchema }),
  ),
  bySku: z.array(
    z.object({
      productId: productIdFormat.schema,
      sku: z.string().nullable(),
      title: z.string(),
      locationName: z.string(),
      weeks: accuracyWeeksSchema,
    }),
  ),
  byBin: z.array(
    z.object({
      storageLocationId: storageLocationIdFormat.schema,
      storageLocationName: z.string().nullable(),
      locationName: z.string(),
      weeks: accuracyWeeksSchema,
    }),
  ),
  byPerson: z.array(
    z.object({
      assignedUserId: z.string().nullable(),
      assignedRole: z.string().nullable(),
      weeks: accuracyWeeksSchema,
    }),
  ),
});
export type CycleCountAnalyticsAccuracy = z.infer<typeof accuracyOutputSchema>;

const varianceGroupBaseSchema = z.object({
  events: z.int(),
  countsRun: z.int(),
  shortage: z.number(),
  overage: z.number(),
});

const varianceOutputSchema = z.object({
  currency: z.string(),
  weekAxis: weekAxisSchema,
  weeks: z.array(
    z.object({
      weekStart: z.date(),
      shortage: z.number(),
      overage: z.number(),
    }),
  ),
  byLocation: z.array(
    varianceGroupBaseSchema.extend({ locationName: z.string() }),
  ),
  byBin: z.array(
    varianceGroupBaseSchema.extend({
      storageLocationId: storageLocationIdFormat.schema,
      storageLocationName: z.string().nullable(),
      locationName: z.string(),
    }),
  ),
  byPerson: z.array(
    varianceGroupBaseSchema.extend({
      assignedUserId: z.string().nullable(),
      assignedRole: z.string().nullable(),
      locationName: z.string().nullable(),
    }),
  ),
  topSkus: z.array(
    z.object({
      productId: productIdFormat.schema,
      sku: z.string().nullable(),
      title: z.string(),
      locationName: z.string(),
      events: z.int(),
      shortage: z.number(),
      overage: z.number(),
    }),
  ),
});
export type CycleCountAnalyticsVariance = z.infer<typeof varianceOutputSchema>;

const approvalRowSchema = z.object({
  cycleCountId: cycleCountIdFormat.schema,
  locationName: z.string(),
  name: z.string().nullable(),
  assignedUserId: z.string().nullable(),
  assignedRole: z.string().nullable(),
  createdAt: z.date(),
  itemsCounted: z.int(),
  varianceItems: z.int(),
  shortage: z.number(),
  overage: z.number(),
});

const approvalsOutputSchema = z.object({
  currency: z.string(),
  counts: z.array(approvalRowSchema),
});
export type CycleCountAnalyticsApprovals = z.infer<
  typeof approvalsOutputSchema
>;

const inputSchema = z.object({
  dateRange: dateRangeSchema,
  timezone: z.string(),
});

export const getCycleCountOverviewAnalytics = unaryRpc({
  name: "getCycleCountOverviewAnalytics" as const,
  input: inputSchema,
  output: overviewOutputSchema,
});

export const getCycleCountAccuracyAnalytics = unaryRpc({
  name: "getCycleCountAccuracyAnalytics" as const,
  input: inputSchema,
  output: accuracyOutputSchema,
});

export const getCycleCountVarianceAnalytics = unaryRpc({
  name: "getCycleCountVarianceAnalytics" as const,
  input: inputSchema,
  output: varianceOutputSchema,
});

export const getCycleCountApprovalsAnalytics = unaryRpc({
  name: "getCycleCountApprovalsAnalytics" as const,
  input: inputSchema,
  output: approvalsOutputSchema,
});
